import Dexie, { type Table } from 'dexie';
import type {
  AppSettings,
  Book,
  BookStatusHistory,
  CoverAsset,
  Goal,
  HomeHighlightContent,
  Note,
  Quote,
  ReadingProgressEntry,
  ReadingSession,
  Series
} from '../domain/entities/types';
import { nowInstant } from '../utils/core';
import type { SyncConflict, SyncCoverConflict, SyncCoverState, SyncEntityState, SyncMetadata, SyncOutboxRecord } from '../sync/types';
import type { EpubAsset, EpubReaderCache } from './epubStorageTypes';

export const LEGACY_DATABASE_NAME = 'leitour';

export const databaseStoresV1 = {
  books: '&id, status, format, normalizedTitle, normalizedAuthor, seriesId, createdAt, updatedAt, finishedOn, [status+updatedAt]',
  coverAssets: '&id, createdAt, updatedAt',
  sessions: '&id, bookId, status, startedAt, finishedAt, startedOn, [bookId+startedAt], [status+startedAt]',
  progressEntries: '&id, bookId, sessionId, kind, occurredOn, recordedAt, [bookId+recordedAt], [bookId+occurredOn]',
  statusHistory: '&id, bookId, changedAt, changedOn, [bookId+changedAt]',
  notes: '&id, bookId, createdAt, updatedAt, [bookId+createdAt]',
  quotes: '&id, bookId, createdAt, updatedAt, [bookId+createdAt]',
  series: '&id, normalizedName, createdAt, updatedAt',
  goals: '&id, &year',
  settings: '&id, activeSessionId'
};

export const databaseStoresV5 = {
  ...databaseStoresV1,
  syncOutbox: '&batchId, status, createdAt, nextAttemptAt, [status+nextAttemptAt]',
  syncEntityStates: '&[entityType+entityId], entityType, entityId, remoteRevision, lastChangeId, deletedAt',
  syncConflicts: '&id, [entityType+entityId], batchId, status, detectedAt',
  syncMetadata: '&id, userId, bootstrapState, lastSuccessfulSyncAt'
};

export const databaseStoresV6 = {
  ...databaseStoresV5,
  syncCoverStates: '&coverId, bookId, sha256, status, updatedAt'
};

export const databaseStoresV7 = {
  ...databaseStoresV6,
  syncCoverConflicts: '&id, bookId, status, detectedAt'
};

export const databaseStoresV8 = {
  ...databaseStoresV7,
  syncOutbox: '&batchId, status, queueOrder, createdAt, nextAttemptAt, [status+nextAttemptAt]'
};

export const databaseStoresV9 = {
  ...databaseStoresV8,
  homeHighlightContent: '&id, updatedAt'
};

export const databaseStoresV10 = {
  ...databaseStoresV9,
  epubAssets: '&bookId',
  epubReaderCache: '&bookId'
};

export class LeitourDatabase extends Dexie {
  books!: Table<Book, string>;
  coverAssets!: Table<CoverAsset, string>;
  sessions!: Table<ReadingSession, string>;
  progressEntries!: Table<ReadingProgressEntry, string>;
  statusHistory!: Table<BookStatusHistory, string>;
  notes!: Table<Note, string>;
  quotes!: Table<Quote, string>;
  series!: Table<Series, string>;
  goals!: Table<Goal, string>;
  settings!: Table<AppSettings, string>;
  homeHighlightContent!: Table<HomeHighlightContent, 'home'>;
  syncOutbox!: Table<SyncOutboxRecord, string>;
  syncEntityStates!: Table<SyncEntityState, [SyncEntityState['entityType'], string]>;
  syncConflicts!: Table<SyncConflict, string>;
  syncMetadata!: Table<SyncMetadata, 'sync'>;
  syncCoverStates!: Table<SyncCoverState, string>;
  syncCoverConflicts!: Table<SyncCoverConflict, string>;
  epubAssets!: Table<EpubAsset, string>;
  epubReaderCache!: Table<EpubReaderCache, string>;

  constructor(name = LEGACY_DATABASE_NAME) {
    super(name);
    const stores = databaseStoresV1;
    this.version(1).stores(stores);
    this.version(2).stores(stores).upgrade(async (transaction) => {
      const books = await transaction.table<Book, string>('books').toArray();
      const entries = await transaction.table<ReadingProgressEntry, string>('progressEntries').toArray();
      for (const book of books) {
        const initial = entries
          .filter((entry) => entry.bookId === book.id)
          .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt) || a.createdAt.localeCompare(b.createdAt))[0];
        if (!initial || initial.isBaseline) continue;
        if (initial.kind === 'pages') {
          await transaction.table('progressEntries').update(initial.id, {
            source: 'baseline', isBaseline: true, previousPage: initial.currentPage, deltaPages: 0
          });
        } else if (initial.kind === 'audio_time') {
          await transaction.table('progressEntries').update(initial.id, {
            source: 'baseline', isBaseline: true, previousMinutes: initial.currentMinutes, deltaMinutes: 0
          });
        } else if (initial.kind === 'audio_percent') {
          await transaction.table('progressEntries').update(initial.id, {
            source: 'baseline', isBaseline: true, previousPercent: initial.currentPercent, deltaPercent: 0
          });
        }
      }
    });
    this.version(3).stores(stores).upgrade(async (transaction) => {
      await transaction.table<Book, string>('books').toCollection().modify((book) => {
        if (book.status === 'finished' && !book.completionOrigin) book.completionOrigin = 'tracked';
      });
    });
    this.version(4).stores(stores).upgrade(async (transaction) => {
      await transaction.table<AppSettings, string>('settings').toCollection().modify((settings) => {
        const legacy = settings as AppSettings & { homeHighlight?: { enabled: boolean; title: string; text: string; reference: string } };
        if (!legacy.homeHighlight) {
          legacy.homeHighlight = { enabled: false, title: 'Salmo do dia', text: '', reference: '' };
        }
      });
    });
    this.version(5).stores(databaseStoresV5);
    // Upgrade aditivo: nenhum dado de domínio ou estado Sync V1 existente é
    // reescrito. A store separa metadados técnicos de capa do backup do usuário.
    this.version(6).stores(databaseStoresV6);
    // Store técnica aditiva; não percorre nem reescreve dados existentes.
    this.version(7).stores(databaseStoresV7);
    this.version(8).stores(databaseStoresV8).upgrade(async (transaction) => {
      const outboxTable = transaction.table<SyncOutboxRecord, string>('syncOutbox');
      const storedOutbox = await outboxTable.toArray();
      const storedOrders = storedOutbox.map(({ queueOrder }) => queueOrder);
      const hasReliableQueueOrder = storedOrders.every((value) =>
        Number.isSafeInteger(value) && value > 0)
        && new Set(storedOrders).size === storedOrders.length;
      const outbox = storedOutbox.sort((left, right) => hasReliableQueueOrder
        ? left.queueOrder - right.queueOrder || left.batchId.localeCompare(right.batchId)
        : left.createdAt.localeCompare(right.createdAt) || left.batchId.localeCompare(right.batchId));
      const ambiguousCreatedAt = new Set<string>();
      if (!hasReliableQueueOrder) {
        for (let index = 1; index < outbox.length; index += 1) {
          if (outbox[index - 1]!.createdAt === outbox[index]!.createdAt) {
            ambiguousCreatedAt.add(outbox[index]!.createdAt);
          }
        }
      }
      for (const [index, record] of outbox.entries()) {
        await outboxTable.update(record.batchId, {
          queueOrder: hasReliableQueueOrder ? record.queueOrder : index + 1
        });
      }
      if (ambiguousCreatedAt.size) {
        const metadataTable = transaction.table<SyncMetadata, 'sync'>('syncMetadata');
        const metadata = await metadataTable.get('sync');
        if (metadata) {
          await metadataTable.update('sync', {
            bootstrapState: 'failed',
            lastError: 'ambiguous_legacy_outbox_order'
          });
        } else if (outbox[0]) {
          await metadataTable.put({
            id: 'sync',
            protocolVersion: outbox[0].batch.protocolVersion,
            canonicalization: outbox[0].batch.canonicalization,
            userId: outbox[0].userId,
            deviceId: outbox[0].batch.deviceId,
            cursor: { lastChangeId: '0' },
            bootstrapState: 'failed',
            remoteRollout: { status: 'not_checked' },
            lastError: 'ambiguous_legacy_outbox_order'
          });
        }
      }
    });
    this.version(9).stores(databaseStoresV9).upgrade(async (transaction) => {
      const settingsTable = transaction.table<AppSettings, string>('settings');
      await settingsTable.toCollection().modify((settings) => {
        const legacy = settings as AppSettings & {
          homeHighlight?: { enabled?: boolean };
          showHomeHighlight?: boolean;
        };
        settings.showHomeHighlight = legacy.showHomeHighlight
          ?? legacy.homeHighlight?.enabled
          ?? true;
        Reflect.deleteProperty(settings, 'homeHighlight');
      });

    });
    // Stores estritamente locais: não participam de domínio, Sync ou backup.
    this.version(10).stores(databaseStoresV10);

    this.on('populate', () => {
      const now = nowInstant();
      return this.settings.add({
        id: 'app',
        theme: 'light',
        locale: 'pt-BR',
        showHomeHighlight: true,
        createdAt: now,
        updatedAt: now
      });
    });
  }
}

export const db = new LeitourDatabase();
